import React from "react";
import {
  Grid,
  MenuItem,
  Stack,
  Button,
  Box,
  IconButton,
  Typography,
  Collapse,
  Divider,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopy from "@mui/icons-material/ContentCopy";
import moment from "moment";
import { useFormik } from "formik";
import {
  CustomFormLabel,
  CustomTextField,
  CustomSelect,
  ControlledCustomSelect,
  CountryCodeSelect,
  CustomCheckbox,
} from "../../../components/formsComponents";
import { fetchActionItemDynamicFormListAsync } from "../../../redux/actionItemDynamicForm/actionItemDynamicFormActions";
import {
  IActionItemDynamicForm,
  IActionItemDynamicFormField,
} from "../../../redux/actionItemDynamicForm/actionItemDynamicForm.types";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import { PhoneTextField } from "../../../components/PhoneTextField/PhoneTextField";
import { StandardCard } from "../../../components/Cards/StandardCard";
import { UsersAutoSearch } from "../../../components/AutoCompleteSearches/UsersAutoSearch";
import { ActionItemCategoryAutoSearch } from "../../../components/AutoCompleteSearches/ActionItemCategoryAutoSearch";
import { MasterStatusAutoSearch } from "../../../components/AutoCompleteSearches/MasterStatusAutoSearch";
import { LeadSourceAutoSearch } from "../../../components/AutoCompleteSearches/LeadSourceAutoSearch";
import {
  IActionItem,
  IActionItemAdditionalContact,
  IActionItemStatus,
  upsertActionItemAsync,
} from "../../../redux/actionItems";
import { useDispatchWrapper } from "../../../hooks";
import { useBillingCompanyContext } from "../../../providers";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { PriorityFilter } from "../../../components/AutoCompleteSearches/PriorityFilter";
import { CountryCodeSelector } from "../../../components/CountrySelector";
import { validatePhoneNumber } from "../../../constants/phoneConfig";
import { ExtLeadStatusSearch } from "../selectors/ExtLeadStatusSearch";
import { DialerCallButton, DialerCallDialog, useDialerCall } from "../../leads/components/DialerCallButton";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { api } from "../../../api/api";
import { showMessage } from "../../../redux/messages/messagesActions";

export interface IChatLeadFormProps {
  initial: IActionItem;
  onSaved: (saved: IActionItem) => void;
  onCancel: () => void;
  renderFooter?: (ctx: {
    isSubmitting: boolean;
    submit: () => void;
    cancel: () => void;
    isEdit: boolean;
  }) => React.ReactNode;
}

const DEFAULT_SOURCE_NAME = "Direct";

const isCancelledOrDead = (actualStatus?: string | null) =>
  ["CANCELLED", "DEAD"].includes((actualStatus || "").toUpperCase());

type LeadStatus = "DEAL" | "LEAD";

/** DEAL/LEAD statuses hide the followup date and auto-fill the (still visible) remark. */
export const getRandomLeadStatusComment = (
  status: LeadStatus,
  customerName?: string,
): string => {
  const comments = [
    ` ${customerName} has confirmed the booking.`
    // " Lead successfully converted into a booked deal.",
    // "Deal confirmed and booking has been completed.",
    // customerName
    //   ? ` ${customerName} has confirmed the booking.`
    //   : "Customer has confirmed the booking.",
    // customerName
    //   ? `${customerName} is now a confirmed customer.`
    //   : "Lead has been successfully booked.",
    // "Booking finalized successfully.",
  ];

  return comments[Math.floor(Math.random() * comments.length)];
};

const formatDisplay = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const formatDateDisplay = (value: any): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const ReadOnlyField: React.FC<{
  label: string;
  value?: React.ReactNode;
  multiline?: boolean;
}> = ({ label, value, multiline }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 1,
      backgroundColor: (theme) => theme.palette.action.hover,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      minHeight: 52,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 500,
        lineHeight: 1.2,
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        mt: 0.25,
        color: value ? "text.primary" : "text.disabled",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// Single form used for BOTH create and edit. Mode is derived from
// `initial.action_item_uuid` — payload shape is identical; backend upserts
// on that key.
export const ChatLeadForm: React.FC<IChatLeadFormProps> = ({
  initial,
  onSaved,
  onCancel,
  renderFooter,
}) => {
  const { country } = useAuth()
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const auth = useAuth();
  const isEdit = Boolean(initial.action_item_uuid);

  const [dynamicFormsList, setDynamicFormsList] = React.useState<
    IActionItemDynamicForm[]
  >([]);
  const [selectedDynamicForm, setSelectedDynamicForm] =
    React.useState<IActionItemDynamicForm | null>(null);
  const [loadingDynamicForms, setLoadingDynamicForms] = React.useState(false);
  const [showDynamicForm, setShowDynamicForm] = React.useState(true);
  const [showActionItemDetails, setShowActionItemDetails] =
    React.useState(false);
  const [showOptional, setShowOptional] = React.useState(false);
  const [showExtras, setShowExtras] = React.useState(false);
  const [showEventBooking, setShowEventBooking] = React.useState(false);
  const [showError, setShowError] = React.useState<any>(null);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [dynamicTouched, setDynamicTouched] = React.useState<
    Record<string, boolean>
  >({});
  const { dialerState, initiateCall, closeDialog } = useDialerCall();
  const { ftDateTime } = useTimezoneFormatter();

  const handleCopyPhoneNumber = React.useCallback(
    async (phoneNumber: string) => {
      try {
        await navigator.clipboard.writeText(phoneNumber);
        dispatch(
          showMessage({
            type: "success",
            displayAs: "snackbar",
            message: "Phone number copied!",
          }),
        );
      } catch {
        dispatch(
          showMessage({
            type: "error",
            displayAs: "snackbar",
            message: "Failed to copy phone number",
          }),
        );
      }
    },
    [dispatch],
  );

  // Call + copy buttons shown at the end of a contact number field
  const renderPhoneActions = (phoneNumber: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <DialerCallButton
        phoneNumber={phoneNumber}
        onCall={initiateCall}
        disabled={dialerState.loading || !phoneNumber}
      />
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleCopyPhoneNumber(phoneNumber);
        }}
        disabled={!phoneNumber}
        sx={{ p: 0.5 }}
        title="Copy phone number"
      >
        <ContentCopy fontSize="small" />
      </IconButton>
    </Box>
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setValues,
    validateForm,
  } = useFormik<IActionItem>({
    initialValues: {
      ...initial,
      // Default to HOT / Direct for new leads and for existing leads saved
      // without a value. The Direct lead_source_uuid is resolved below.
      priority: initial.priority || "HOT",
      source: initial.source || (initial.lead_source_uuid ? initial.source : DEFAULT_SOURCE_NAME),
      form_filled:
        initial.form_filled || (initial as any)?.extras?.form_filled || {},
      ...(!initial.country_code && {
        country_code: country.dialCode,
        country_iso_code: country.code,
      }),
    },
    enableReinitialize: true,
    validate: (v) => {
      const e: Record<string, string> = {};
      if (!v.name?.trim()) e.name = "Name is required";
      if (!v.mobile_no?.trim()) e.mobile_no = "Contact number is required";
      // else {
      //   const phoneResult = validatePhoneNumber(v.country_code || country.dialCode, v.mobile_no);
      //   if (!phoneResult.isValid) e.mobile_no = phoneResult.error || "Invalid phone number";
      // }
      if (!v.status) e.status = "Status is required";
      if (!v.assigned_to_uuid)
        e.assigned_to_uuid = "Assigned To is required";
      if (isCancelledOrDead(v.actual_status) && !v.cancellation_dead_reason) {
        e.cancellation_dead_reason =
          "Reason is required for Cancelled / Dead status";
      }
      if (
        (v.actual_status || "").toUpperCase() !== "CANCELLED" &&
        !v.remarks
      )
        e.remarks = "Remarks is required";

      if (v.email?.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(v.email.trim())) {
          e.email = "Please enter a valid email address";
        }
      }

      const isRoomBooking =
        (v.category_code || "").trim().toUpperCase() === "ROOM_BOOKING";
      if (isRoomBooking) {
        const cIn = (v.form_filled as any)?.check_in_date;
        const cOut = (v.form_filled as any)?.check_out_date;
        if (cIn && cOut) {
          const mIn = moment(
            cIn,
            ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
            true,
          );
          const mOut = moment(
            cOut,
            ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
            true,
          );
          if (mIn.isValid() && mOut.isValid() && mOut.isBefore(mIn, "day")) {
            e.check_out_date = "Check out date cannot be before check in date";
          }
        }

        const dynamicEmail = (v.form_filled as any)?.email_address;
        if (
          dynamicEmail &&
          typeof dynamicEmail === "string" &&
          dynamicEmail.trim()
        ) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(dynamicEmail.trim())) {
            e.email_address = "Please enter a valid email address";
          }
        }

        const dynamicMobile = (v.form_filled as any)?.mobile_number;
        if (
          dynamicMobile &&
          typeof dynamicMobile === "string" &&
          dynamicMobile.trim()
        ) {
          if (!/^\+?[0-9]+$/.test(dynamicMobile.trim())) {
            e.mobile_number = "Mobile number should only contain numbers and '+' symbol";
          }
        }
      }
      return e;
    },
    onSubmit: (vals, helpers) => {
      // Build form_filled object with all fields from selectedDynamicForm when category is ROOM_BOOKING
      const isRoomBooking =
        (vals.category_code || "").trim().toUpperCase() === "ROOM_BOOKING";
      const finalFormFilled: Record<string, any> = {};

      if (
        isRoomBooking &&
        selectedDynamicForm?.fields &&
        selectedDynamicForm.fields.length > 0
      ) {
        selectedDynamicForm.fields.forEach((field) => {
          const rawVal = (vals.form_filled as any)?.[field.field_key];
          if (rawVal !== undefined && rawVal !== null && rawVal !== "") {
            const isDateField =
              (field.field_type || "").toUpperCase() === "DATE" ||
              field.field_key === "check_in_date" ||
              field.field_key === "check_out_date";
            if (isDateField) {
              const parsed = moment(
                rawVal,
                ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
                true,
              );
              finalFormFilled[field.field_key] = parsed.isValid()
                ? parsed.format("DD-MM-YYYY")
                : String(rawVal);
            } else {
              finalFormFilled[field.field_key] =
                typeof rawVal === "number" ? String(rawVal) : rawVal;
            }
          } else {
            finalFormFilled[field.field_key] = null;
          }
        });
      } else if (isRoomBooking && vals.form_filled) {
        Object.entries(vals.form_filled).forEach(([k, v]) => {
          finalFormFilled[k] =
            v !== undefined && v !== null && v !== ""
              ? typeof v === "number"
                ? String(v)
                : v
              : null;
        });
      }

      const payload: IActionItem = {
        ...vals,
        ...(isRoomBooking ? { form_filled: finalFormFilled } : {}),
      };

      dispatch(
        upsertActionItemAsync(payload, (isSuccess) => {
          helpers.setSubmitting(false);
          if (isSuccess) onSaved(payload);
        }),
      );
    },
  });

  const handleFormSubmit = async () => {
    const formErrors = await validateForm();
    setSubmitAttempted(true);
    if (Object.keys(formErrors).length > 0) {
      setShowError(formErrors);
      return;
    }
    handleSubmit();
  };

  // Auto-fill assigned_to with current user if not already set
  React.useEffect(() => {
    if (!values.assigned_to_uuid && !values.assigned_to_name) {
      setFieldValue("assigned_to_uuid", auth.user.userUuid || "");
      setFieldValue(
        "assigned_to_name",
        auth.user.full_name || auth.user.name || "",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the "Direct" lead source uuid when the lead has no source yet
  // (new lead, or an existing lead saved with a null source).
  React.useEffect(() => {
    const outletUuid =
      companyOutlet.comapny?.company_outlet_uuid || initial.company_outlet_uuid;
    const hasOtherSource =
      Boolean(initial.source) &&
      !initial.source?.toLowerCase().includes(DEFAULT_SOURCE_NAME.toLowerCase());
    if (!outletUuid || initial.lead_source_uuid || hasOtherSource) return;

    let cancelled = false;
    const fetchDirectSource = async () => {
      try {
        const res = await api.get(
          `/leads/get-lead-source?company_outlet_uuid=${outletUuid}&status=ACTIVE`,
        );
        const directSource = (res.data.data || []).find((s: any) =>
          s.source_name?.toLowerCase().includes(DEFAULT_SOURCE_NAME.toLowerCase()),
        );
        if (cancelled || !directSource) return;
        // Don't clobber a source the user picked while the request was in flight.
        setValues((prev) =>
          prev.lead_source_uuid
            ? prev
            : {
                ...prev,
                lead_source_uuid: directSource.lead_source_uuid,
                source: directSource.source_name,
              },
        );
      } catch {
        // silently ignore — the "Direct" name is still set as the fallback
      }
    };
    fetchDirectSource();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyOutlet.comapny?.company_outlet_uuid,
    initial.action_item_uuid,
    initial.lead_source_uuid,
    initial.source,
  ]);

  // DEAL/LEAD hide the followup date; the remark stays visible, so auto-fill
  // it with a random status comment (does not overwrite an existing remark).
  React.useEffect(() => {
    const statusUpper = (values.actual_status || "").toUpperCase();
    if (
      (statusUpper === "DEAL" || statusUpper === "LEAD") &&
      !values.remarks
    ) {
      setFieldValue(
        "remarks",
        getRandomLeadStatusComment(statusUpper as LeadStatus, values.name),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.actual_status]);

  const additionalContacts = values.additional_contact_number || [];
  const extras: Record<string, any> | null = React.useMemo(() => {
    const ex = (values as any)?.extras;
    if (!ex || typeof ex !== "object" || Array.isArray(ex)) return null;
    return ex as Record<string, any>;
  }, [values]);

  const extrasEntries = React.useMemo(() => {
    if (!extras) return [];
    return Object.entries(extras)
      .filter(([k, v]) => k && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [extras]);

  const disableFormByStatus = false;

  const toLabel = (key: string) =>
    key
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const addAdditionalContact = () => {
    const next: IActionItemAdditionalContact = {
      country_code: country.dialCode,
      country_iso_code: country.code,
      contact_number: "",
      name: "",
    };
    setFieldValue("additional_contact_number", [...additionalContacts, next]);
  };

  const updateAdditionalContact = (
    index: number,
    patch: Partial<IActionItemAdditionalContact>,
  ) => {
    const next = additionalContacts.map((c, i) =>
      i === index ? { ...c, ...patch } : c,
    );
    setFieldValue("additional_contact_number", next);
  };

  const removeAdditionalContact = (index: number) => {
    const next = additionalContacts.filter((_, i) => i !== index);
    setFieldValue("additional_contact_number", next);
  };

  const showCancellation = isCancelledOrDead(values.actual_status);
  const isCancelled =
    (values.actual_status || "").toUpperCase() === "CANCELLED";
  const isDealOrLead = ["DEAL", "LEAD"].includes(
    (values.actual_status || "").toUpperCase(),
  );
  const isRoomBooking =
    (values.category_code || "").trim().toUpperCase() === "ROOM_BOOKING";

  React.useEffect(() => {
    const outletUuid =
      companyOutlet.comapny?.company_outlet_uuid || initial.company_outlet_uuid;
    if (!outletUuid) return;

    setLoadingDynamicForms(true);
    const dateRange = {
      fromDate: "2000-01-01",
      toDate: new Date().toISOString().split("T")[0],
    };

    dispatch(
      fetchActionItemDynamicFormListAsync(
        outletUuid,
        1,
        100,
        100,
        "ACTIVE",
        dateRange,
        undefined,
        undefined,
        undefined,
        (isSuccess, data) => {
          setLoadingDynamicForms(false);
          if (isSuccess && data) {
            setDynamicFormsList(data);
          }
        },
      ),
    );
  }, [
    companyOutlet.comapny?.company_outlet_uuid,
    initial.company_outlet_uuid,
    dispatch,
  ]);

  React.useEffect(() => {
    if (!isRoomBooking || !dynamicFormsList || dynamicFormsList.length === 0) {
      setSelectedDynamicForm(null);
      return;
    }

    const matched = dynamicFormsList.find(
      (f) => f.category_code?.trim().toUpperCase() === "ROOM_BOOKING",
    );
    setSelectedDynamicForm(matched || dynamicFormsList[0]);
  }, [dynamicFormsList, values.category_code, isRoomBooking]);

  React.useEffect(() => {
    if (!selectedDynamicForm?.fields?.length) return;

    const currentFilled = (values.form_filled as any) || {};
    let hasNew = false;
    const updated = { ...currentFilled };

    selectedDynamicForm.fields.forEach((field) => {
      if (
        updated[field.field_key] === undefined ||
        updated[field.field_key] === null ||
        updated[field.field_key] === ""
      ) {
        if (
          extras &&
          extras[field.field_key] !== undefined &&
          extras[field.field_key] !== null
        ) {
          let extraVal = extras[field.field_key];
          const isDateField =
            (field.field_type || "").toUpperCase() === "DATE" ||
            field.field_key === "check_in_date" ||
            field.field_key === "check_out_date";
          if (isDateField) {
            const parsed = moment(
              extraVal,
              ["YYYY-MM-DD", "DD-MM-YYYY", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
              true,
            );
            if (parsed.isValid()) {
              extraVal = parsed.format("DD-MM-YYYY");
            }
          }
          updated[field.field_key] = String(extraVal);
          hasNew = true;
        } else if (
          field.field_key === "guest_name" &&
          (values.name || initial.name)
        ) {
          updated[field.field_key] = values.name || initial.name;
          hasNew = true;
        } else if (
          (field.field_key === "mobile_number" || field.field_key === "phone") &&
          (values.mobile_no || initial.mobile_no)
        ) {
          updated[field.field_key] = values.mobile_no || initial.mobile_no;
          hasNew = true;
        } else if (
          (field.field_key === "email_address" || field.field_key === "email") &&
          (values.email || initial.email)
        ) {
          updated[field.field_key] = values.email || initial.email;
          hasNew = true;
        } else {
          const defaultOpt = field.options?.find((o) => o.is_default);
          if (defaultOpt) {
            updated[field.field_key] = defaultOpt.option_value;
            hasNew = true;
          }
        }
      }
    });

    if (hasNew) {
      setFieldValue("form_filled", updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDynamicForm, extras]);

  const dynamicFormSections = React.useMemo(() => {
    if (!selectedDynamicForm?.fields) return {};
    const map: Record<string, IActionItemDynamicFormField[]> = {};
    const visibleFields = selectedDynamicForm.fields
      .filter((f) => f.is_visible !== false)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    visibleFields.forEach((field) => {
      const sec = field.section_name?.trim() || "Form Details";
      if (!map[sec]) map[sec] = [];
      map[sec].push(field);
    });
    return map;
  }, [selectedDynamicForm]);

  const handleDynamicFieldChange = (key: string, val: any) => {
    setFieldValue(`form_filled.${key}`, val);
  };

  const renderDynamicField = (field: IActionItemDynamicFormField) => {
    const rawVal = (values.form_filled as any)?.[field.field_key];
    const val = rawVal !== undefined && rawVal !== null ? String(rawVal) : "";
    const isDisabled = field.is_editable === false;
    const typeUpper = (field.field_type || "TEXT").toUpperCase();
    const effectiveType =
      field.field_key === "mobile_number"
        ? "PHONE"
        : field.field_key === "email_address"
        ? "EMAIL"
        : typeUpper;

    switch (effectiveType) {
      case "TEXTAREA":
        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomTextField
              fullWidth
              multiline
              minRows={1}
              placeholder={
                field.placeholder || `Enter ${field.field_label.toLowerCase()}`
              }
              value={val}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
              disabled={isDisabled}
            />
          </Box>
        );

      case "SELECT":
        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomSelect
              fullWidth
              value={val}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
              disabled={isDisabled}
            >
              <MenuItem value="">
                <em>Select {field.field_label}</em>
              </MenuItem>
              {field.options?.map((opt) => (
                <MenuItem key={opt.option_value} value={opt.option_value}>
                  {opt.option_label}
                </MenuItem>
              ))}
            </CustomSelect>
          </Box>
        );

      case "RADIO":
        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <RadioGroup
              row
              value={val}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
            >
              {field.options?.map((opt) => (
                <FormControlLabel
                  key={opt.option_value}
                  value={opt.option_value}
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={opt.option_label}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case "CHECKBOX":
        return (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <CustomCheckbox
                  checked={val === "true" || Boolean(rawVal)}
                  onChange={(e: any) =>
                    handleDynamicFieldChange(
                      field.field_key,
                      e.target.checked ? "true" : "false",
                    )
                  }
                  disabled={isDisabled}
                />
              }
              label={field.field_label}
            />
          </Box>
        );

      case "DATE": {
        let pickerVal: string | null = null;
        if (val) {
          const parsed = moment(
            val,
            ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
            true,
          );
          if (parsed.isValid()) {
            pickerVal = parsed.format("YYYY-MM-DD");
          } else {
            const fallback = moment(val);
            pickerVal = fallback.isValid() ? fallback.format("YYYY-MM-DD") : val;
          }
        }

        // Check in / Check out date validation and boundary limits
        let dateMin: any = undefined;
        let dateMax: any = undefined;
        let dateErrorMsg: string | undefined = undefined;

        if (field.field_key === "check_out_date") {
          const inVal = (values.form_filled as any)?.check_in_date;
          if (inVal) {
            const mIn = moment(
              inVal,
              ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
              true,
            );
            if (mIn.isValid()) {
              dateMin = mIn;
              if (val) {
                const mOut = moment(
                  val,
                  ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
                  true,
                );
                if (mOut.isValid() && mOut.isBefore(mIn, "day")) {
                  dateErrorMsg = "Check out date cannot be before check in date";
                }
              }
            }
          }
        } else if (field.field_key === "check_in_date") {
          const outVal = (values.form_filled as any)?.check_out_date;
          if (outVal) {
            const mOut = moment(
              outVal,
              ["DD-MM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.SSSZ"],
              true,
            );
            if (mOut.isValid()) {
              dateMax = mOut;
            }
          }
        }

        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomDatePicker
              // placeholder={field.placeholder || field.field_label}
              fullWidth
              disabled={isDisabled}
              value={pickerVal}
              minDate={dateMin}
              maxDate={dateMax}
              errorMessage={dateErrorMsg}
              error={Boolean(dateErrorMsg)}
              inputFormat="DD-MM-YYYY"
              onChange={(_dateStr, _dateWithTime, momentObj) => {
                if (!momentObj || !momentObj.isValid()) {
                  handleDynamicFieldChange(field.field_key, null);
                } else {
                  handleDynamicFieldChange(
                    field.field_key,
                    momentObj.format("DD-MM-YYYY"),
                  );
                }
              }}
            />
          </Box>
        );
      }

      case "EMAIL": {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const formikEmailErr = (errors as any)?.[field.field_key];
        const isTouched =
          Boolean((touched as any)?.[field.field_key]) ||
          Boolean(dynamicTouched[field.field_key]) ||
          submitAttempted;
        const isInvalid = Boolean(
          val && val.trim() && !emailRegex.test(val.trim()),
        );
        const showError = isTouched && (Boolean(formikEmailErr) || isInvalid);
        const errorText = showError
          ? formikEmailErr || "Please enter a valid email address"
          : undefined;

        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomTextField
              fullWidth
              type="email"
              placeholder={
                field.placeholder || `Enter ${field.field_label.toLowerCase()}`
              }
              value={val}
              error={showError}
              helperText={errorText}
              onBlur={() => {
                setDynamicTouched((prev) => ({
                  ...prev,
                  [field.field_key]: true,
                }));
              }}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
              disabled={isDisabled}
            />
          </Box>
        );
      }

      case "NUMBER":
        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomTextField
              fullWidth
              type="number"
              placeholder={
                field.placeholder || `Enter ${field.field_label.toLowerCase()}`
              }
              value={val}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
              disabled={isDisabled}
            />
          </Box>
        );

      case "PHONE": {
        const phoneErrorMsg = (errors as any)?.[field.field_key];
        const isTouched =
          Boolean((touched as any)?.[field.field_key]) ||
          Boolean(dynamicTouched[field.field_key]) ||
          submitAttempted;
        const hasPhoneError = isTouched && Boolean(phoneErrorMsg);
        const phoneHelperText = hasPhoneError ? phoneErrorMsg : undefined;

        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomTextField
              fullWidth
              placeholder={
                field.placeholder || `Enter ${field.field_label.toLowerCase()}`
              }
              value={val}
              error={hasPhoneError}
              helperText={phoneHelperText}
              inputProps={{
                inputMode: "tel",
                maxLength: 20,
              }}
              onBlur={() => {
                setDynamicTouched((prev) => ({
                  ...prev,
                  [field.field_key]: true,
                }));
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                    "Home",
                    "End",
                  ].includes(e.key) ||
                  ((e.ctrlKey || e.metaKey) &&
                    ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase()))
                ) {
                  return;
                }
                // Allow '+' symbol (for country code)
                if (e.key === "+" || e.key === "Add") {
                  const target = e.target as HTMLInputElement;
                  if (!target.value.includes("+") && (target.selectionStart === 0 || !target.value)) {
                    return;
                  }
                  e.preventDefault();
                  return;
                }
                // Disallow string characters (letters/symbols)
                if (!/^[0-9]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e: any) => {
                // Allow '+' symbol at start and numbers only
                let cleaned = e.target.value.replace(/[^\d+]/g, "");
                if (cleaned.startsWith("+")) {
                  cleaned = "+" + cleaned.slice(1).replace(/\+/g, "");
                } else {
                  cleaned = cleaned.replace(/\+/g, "");
                }
                handleDynamicFieldChange(field.field_key, cleaned);
              }}
              disabled={isDisabled}
            />
          </Box>
        );
      }

      case "TEXT":
      default:
        return (
          <Box>
            <CustomFormLabel>{field.field_label}</CustomFormLabel>
            <CustomTextField
              fullWidth
              placeholder={
                field.placeholder || `Enter ${field.field_label.toLowerCase()}`
              }
              value={val}
              onChange={(e: any) =>
                handleDynamicFieldChange(field.field_key, e.target.value)
              }
              disabled={isDisabled}
            />
          </Box>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* ===== FIRST SECTION (no StandardCard wrapper) ===== */}
      {/* Row 1 — identity + status (5 equal-feel columns) */}
      <StandardCard
        heading="External Lead Details"
        rightHeading={
          isEdit && (values.create_ts || values.insert_ts) ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              justifyContent="flex-end"
              rowGap={1}
            >
              {/* Priority badge — moved here from the header actions bar */}
              {values.priority && (
                <Chip
                  size="small"
                  label={values.priority}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    fontSize: "0.72rem",
                    height: 24,
                    ...(String(values.priority).toUpperCase() === "HOT"
                      ? { bgcolor: "error.main", color: "error.contrastText" }
                      : String(values.priority).toUpperCase() === "COLD"
                      ? { bgcolor: "info.main", color: "info.contrastText" }
                      : { bgcolor: "grey.300", color: "text.primary" }),
                  }}
                />
              )}
              {values.create_ts && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Created: ${ftDateTime(values.create_ts)}`}
                  sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                />
              )}
              {values.insert_ts && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={`Last Updated: ${ftDateTime(values.insert_ts)}`}
                  sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                />
              )}
            </Stack>
          ) : undefined
        }
      >
        <Grid container spacing={1} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6} md={4} lg={2} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "180px" } }}>
            <CustomTextField
              label="Name"
              fullWidth
              name="name"
              value={values.name}
              onChange={handleChange}
              error={!!((touched.name || submitAttempted) && errors.name)}
              helperText={(touched.name || submitAttempted) && errors.name}
              placeholder="Enter name"
            />
          </Grid>

          <Grid item xs={4} sm={4} md={2} lg={1.2} sx={{ flexGrow: 1, minWidth: "110px" }}>
            <CountryCodeSelector
              sx={{ minHeight: 36 }}
              label="Country Code"
              value={values.country_iso_code}
              onChange={(newCountry) => {
                setValues((prev) => ({
                  ...prev,
                  country_iso_code: newCountry.code,
                  country_code: newCountry.dialCode,
                }))
              }}
            />
          </Grid>

          <Grid item xs={8} sm={8} md={4} lg={2} sx={{ flexGrow: 1, minWidth: { xs: "calc(100% - 120px)", sm: "170px" } }}>
            <PhoneTextField
              label="Contact Number"
              fullWidth
              name="mobile_no"
              value={values.mobile_no.replace(country.dialCode, "")}
              onChange={(val: string) => setFieldValue("mobile_no", val)}
              error={!!((touched.mobile_no || submitAttempted) && errors.mobile_no)}
              helperText={(touched.mobile_no || submitAttempted) ? (errors.mobile_no as string) : ""}
              InputProps={{
                endAdornment: renderPhoneActions(values.mobile_no),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}>
            <CustomTextField
              label="Email"
              fullWidth
              type="email"
              name="email"
              value={values.email || ""}
              onChange={handleChange}
              error={!!((touched.email || submitAttempted) && errors.email)}
              helperText={
                (touched.email || submitAttempted)
                  ? (errors.email as string)
                  : ""
              }
              placeholder="Enter email"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "180px" } }}>
            <LeadSourceAutoSearch
              label="Source"
              value={values.lead_source_uuid || ""}
              source_name={values.source || ""}
              disabled={disableFormByStatus}
              onSelect={(newValue: any) => {
                setFieldValue(
                  "lead_source_uuid",
                  newValue?.lead_source_uuid ?? "",
                );
                setFieldValue(
                  "source",
                  newValue?.source_name ?? "",
                );
              }}
              hideAddButton={true}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}>
            <ActionItemCategoryAutoSearch
              value={{
                category_code: values.category_code,
                category_name: values.category_name,
              }}
              onSelect={(opt) => {
                setFieldValue("category_code", opt?.category_code || "");
                setFieldValue("category_name", opt?.category_name || "");
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "180px" } }}>
            <PriorityFilter
              value={values.priority}
              onChange={(val) => setFieldValue("priority", val)}
            />

          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}>
            <UsersAutoSearch
              label="Handled By Employee"
              showTopLabel={false}
              value={{
                user_uuid: values.assigned_to_uuid || "",
                user_name: values.assigned_to_name || "",
              }}
              error={
                (touched.assigned_to_uuid || submitAttempted)
                  ? (errors.assigned_to_uuid as string)
                  : ""
              }
              onSelect={(u: any) => {
                setValues(prev => ({
                  ...prev,
                  assigned_to_uuid: u?.user_uuid || "",
                  assigned_to_name: u?.user_name || `${u?.first_name || ""} ${u?.last_name || ""}`.trim(),
                }))
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}>
            <ExtLeadStatusSearch
              value={values.drive_status_uuid || null}
              statusName={values.status || ""}
              onChange={(newValue) => {
                if (newValue) {
                  setValues((prev) => ({
                    ...prev,
                    status: newValue.name as IActionItemStatus,
                    actual_status: newValue.actual_status,
                    drive_status_uuid: newValue.external_status_uuid || ""
                  }))
                } else {
                  setValues((prev) => ({
                    ...prev,
                    status: "ACTIVE" as IActionItemStatus,
                    actual_status: "",
                    drive_status_uuid: ""
                  }))
                }
              }}
              error={((touched.status || submitAttempted) && errors.status) as string}
            />

          </Grid>

          {/* <MasterStatusAutoSearch
            name="status"
            value={values.status}
            company_outlet_uuid={companyOutlet.comapny.company_outlet_uuid}
            error={!!((touched.status || submitAttempted) && errors.status)}
            helperText={(touched.status || submitAttempted) ? (errors.status as string) : ""}
            onSelect={(newValue: any) => {
              setFieldValue("status", newValue?.name || "");
              setFieldValue(
                "actual_status",
                newValue?.actual_status || "",
              );
              setFieldValue(
                "drive_status_uuid",
                newValue?.drive_status_uuid ||
                newValue?.status_uuid ||
                "",
              );
            }}
              */}
          {showCancellation && (
            <Grid item xs={12}>
              <CustomFormLabel>Cancellation / Dead Reason</CustomFormLabel>
              <CustomTextField
                fullWidth
                multiline
                minRows={2}
                name="cancellation_dead_reason"
                value={values.cancellation_dead_reason || ""}
                onChange={handleChange}
                error={
                  !!(
                    (touched.cancellation_dead_reason || submitAttempted) &&
                    errors.cancellation_dead_reason
                  )
                }
                helperText={
                  (touched.cancellation_dead_reason || submitAttempted)
                    ? errors.cancellation_dead_reason
                    : ""
                }
                placeholder="Reason for cancellation / dead status"
              />
            </Grid>
          )}
        </Grid>
      </StandardCard>

      {/* ===== ACTION ITEM DETAILS ===== */}
      <StandardCard
        heading="Action Item Details"
        sx={{ mt: 1 }}
        onHeaderClick={() => setShowActionItemDetails((prev) => !prev)}
        hideHeadingDivider={!showActionItemDetails}
        rightHeading={
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowActionItemDetails((prev) => !prev);
            }}
          >
            {showActionItemDetails ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        }
      >
        <Collapse in={showActionItemDetails} timeout="auto" unmountOnExit>
          {/* Read-only fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ReadOnlyField label="Action Item" value={values.item} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadOnlyField
                label="Recommendation"
                value={values.recommendation || ""}
                multiline
              />
            </Grid>
          </Grid>

          {/* Editable fields */}
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <CustomFormLabel>Description</CustomFormLabel>
              <CustomTextField
                fullWidth
                multiline
                minRows={2}
                name="description"
                value={values.description}
                onChange={handleChange}
                placeholder="Enter description"
              />
            </Grid>


          </Grid>
        </Collapse>
      </StandardCard>

      {/* ===== DYNAMIC FORM (ROOM BOOKING ONLY) ===== */}
      {isRoomBooking && selectedDynamicForm && (
        <StandardCard
          heading={`${(values.category_name || selectedDynamicForm?.category_name || values.category_code || "Room Booking").replace(/_/g, " ")} FORM`}
          sx={{ mt: 1 }}
          onHeaderClick={() => setShowDynamicForm((prev) => !prev)}
          hideHeadingDivider={!showDynamicForm}
          rightHeading={
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                setShowDynamicForm((prev) => !prev);
              }}
            >
              {showDynamicForm ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          }
        >
          <Collapse in={showDynamicForm} timeout="auto" unmountOnExit>
            {loadingDynamicForms ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box>
                {Object.entries(dynamicFormSections).map(
                  ([sectionName, secFields], sIdx) => (
                    <Box key={sectionName} sx={{ mt: sIdx > 0 ? 3 : 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          mb: 1.5,
                          fontSize: "0.85rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {sectionName}
                      </Typography>
                      <Grid container spacing={2}>
                        {secFields.map((field) => {
                          const isTextArea =
                            field.field_type?.toUpperCase() === "TEXTAREA";
                          const isNotes =
                            field.field_key === "notes_special_requests";
                          return (
                            <Grid
                              item
                              xs={12}
                              sm={isNotes ? 6 : isTextArea ? 12 : 6}
                              md={isNotes ? 8 : isTextArea ? 8 : 4}
                              key={field.field_key}
                            >
                              {renderDynamicField(field)}
                            </Grid>
                          );
                        })}
                      </Grid>
                      {sIdx < Object.keys(dynamicFormSections).length - 1 && (
                        <Divider sx={{ mt: 2.5 }} />
                      )}
                    </Box>
                  ),
                )}
              </Box>
            )}
          </Collapse>
        </StandardCard>
      )}

      {/* ===== EVENT & BOOKING (all fields read-only) - COMMENTED OUT ===== */}

      <StandardCard
        heading="Event & Booking"
        sx={{ mt: 1 }}
        onHeaderClick={() => setShowEventBooking((prev) => !prev)}
        hideHeadingDivider={!showEventBooking}
        rightHeading={
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowEventBooking((prev) => !prev);
            }}
          >
            {showEventBooking ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        }
      >
        <Collapse in={showEventBooking} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Event Name"
                value={values.event_type || values.other_event_name || ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Event Time"
                value={values.event_time || ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Preferred Venue"
                value={values.preferred_venue || ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Date of Visit"
                value={formatDateDisplay(values.date_of_visit)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Function Date"
                value={formatDateDisplay(values.function_date)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Booking Date"
                value={formatDateDisplay(values.booking_date)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <ReadOnlyField
                label="Function Month"
                value={values.function_month || ""}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <ReadOnlyField
                label="Function Year"
                value={values.function_year || ""}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <ReadOnlyField
                label="Number of Rooms"
                value={formatDisplay(values.number_of_rooms)}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <ReadOnlyField
                label="Number of Guests"
                value={formatDisplay(values.number_of_guests)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Estimated Rough Revenue"
                value={formatDisplay(values.estimated_rough_revenue)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadOnlyField
                label="Guest Budget"
                value={formatDisplay(values.guest_budget)}
              />
            </Grid>
          </Grid>
        </Collapse>
      </StandardCard>

      {/* ===== ADDITIONAL DETAILS (EXTRAS) ===== */}
      {extrasEntries.length > 0 && (
        <StandardCard
          heading="Extras"
          sx={{ mt: 1 }}
          onHeaderClick={() => setShowExtras((prev) => !prev)}
          hideHeadingDivider={!showExtras}
          rightHeading={
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                setShowExtras((prev) => !prev);
              }}
            >
              {showExtras ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          }
        >
          <Collapse in={showExtras} timeout="auto" unmountOnExit>
            <Grid container spacing={1}>
              {extrasEntries.map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <ReadOnlyField
                    label={toLabel(key)}
                    value={formatDisplay(value)}
                    multiline={String(value || "").length > 60}
                  />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </StandardCard>
      )}

      {/* ===== NOTES / COMMENTS ===== */}
      {!isCancelled && (
        <StandardCard heading="Notes / Followups" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {!isDealOrLead && (
              <Grid item xs={12} sm={6} md={4} lg={3} sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}>
                <CustomDatePicker
                  placeholder="Followup Date"
                  fullWidth
                  value={values.followup_date || null}
                  onChange={(value: any) =>
                    setFieldValue("followup_date", value || null)
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <CustomFormLabel>Remark</CustomFormLabel>
              <CustomTextField
                fullWidth
                multiline
                minRows={3}
                name="remarks"
                value={values.remarks || ""}
                onChange={handleChange}
                error={!!((touched.remarks || submitAttempted) && errors.remarks)}
                helperText={(touched.remarks || submitAttempted) ? (errors.remarks as string) : ""}
                placeholder="Enter remark"
              />
            </Grid>
          </Grid>
        </StandardCard>
      )}

      {/* ===== OPTIONAL DETAILS (collapsible) ===== */}
      <StandardCard
        heading="Optional Details"
        sx={{ mt: 1, mb: 1}}
        onHeaderClick={() => setShowOptional((prev) => !prev)}
        hideHeadingDivider={!showOptional}
        rightHeading={
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowOptional((prev) => !prev);
            }}
          >
            {showOptional ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        }
      >
        <Collapse in={showOptional} timeout="auto" unmountOnExit>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Additional Contacts
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addAdditionalContact}
              >
                Add Contact
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {additionalContacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No additional contacts added.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {additionalContacts.map((contact, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="flex-end"
                  >
                    <Grid item xs={3} md={1.5}>
                      <CustomFormLabel>Code</CustomFormLabel>
                      <CountryCodeSelector
                        sx={{ minHeight: 36 }}
                        value={contact.country_iso_code}
                        onChange={(val) =>
                          updateAdditionalContact(index, {
                            country_code: val.dialCode,
                            country_iso_code: val.code
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={9} md={3.5}>
                      <CustomFormLabel>Contact Number</CustomFormLabel>
                      <PhoneTextField
                        fullWidth
                        name={`additional_contact_number.${index}.contact_number`}
                        value={contact.contact_number}
                        onChange={(val: string) =>
                          updateAdditionalContact(index, {
                            contact_number: val,
                          })
                        }
                        InputProps={{
                          endAdornment: renderPhoneActions(contact.contact_number),
                        }}
                      />
                    </Grid>
                    <Grid item xs={10} md={6}>
                      <CustomFormLabel>Name</CustomFormLabel>
                      <CustomTextField
                        fullWidth
                        name={`additional_contact_number.${index}.name`}
                        value={contact.name}
                        onChange={(e) =>
                          updateAdditionalContact(index, {
                            name: e.target.value,
                          })
                        }
                        placeholder="Enter name"
                      />
                    </Grid>
                    <Grid item xs={2} md={1}>
                      <IconButton
                        color="error"
                        onClick={() => removeAdditionalContact(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </StandardCard>

      {renderFooter ? (
        renderFooter({
          isSubmitting,
          submit: handleFormSubmit,
          cancel: onCancel,
          isEdit,
        })
      ) : (
        <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2}>
          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="contained" disabled={isSubmitting} onClick={handleFormSubmit}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </Stack>
      )}
      <Snackbar
        open={!!showError}
        autoHideDuration={5000}
        onClose={() => setShowError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setShowError(null)}
          severity="error"
          sx={{ width: 400 }}
        >
          <List dense sx={{ p: 0 }}>
            {showError &&
              Object.values(showError).map((msg, index) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <ListItemText primary={String(msg)} />
                </ListItem>
              ))}
          </List>
        </Alert>
      </Snackbar>

      <DialerCallDialog
        open={dialerState.open}
        type={dialerState.type}
        message={dialerState.message}
        onClose={closeDialog}
      />
    </form>
  );
};